import { Box, Chip, Grid, Stack, TextField } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, FieldBox } from '@tom-ui/ui'
import {
  IFormStore,
  notAllowPlusMinusPointSign,
  onlyAllowPositiveInt,
  useRegisterSubFormStore,
} from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import {
  CargoDetailsSubFormStore,
  CargoDetailsSubFormType,
} from '../stores/CargoDetailsSubFormStore'

export const RegisteredCargoDetailsSubForm = observer(
  <T,>({
    formStore,
    name,
    initialValues,
  }: {
    formStore: IFormStore<T>
    name: keyof T
    initialValues?: Partial<CargoDetailsSubFormType>
  }) => {
    const store = useRegisterSubFormStore(
      formStore,
      name,
      new CargoDetailsSubFormStore(initialValues),
    )

    return <CargoDetailsSubForm store={store} />
  },
)

export const CargoDetailsSubForm = observer(
  ({
    store,
    isFromRestowPage,
  }: {
    store: CargoDetailsSubFormStore
    isFromRestowPage?: boolean
  }) => {
    const { t } = useTranslate()

    return (
      <Stack>
        <FieldBox
          label={t(`cargoStatus`, 'Cargo status')}
          rightRender={
            <>
              <Box>
                <Chip
                  label={t('full', 'Full')}
                  color={!store.isEmpty.value ? 'primary' : undefined}
                  sx={{ padding: '0 1rem', mr: '.75rem' }}
                  variant='filled'
                  data-cy='cargo-status-full-btn'
                  onClick={() => store.isEmpty.setValue(false)}
                />
                <Chip
                  label={t('empty', 'Empty')}
                  color={store.isEmpty.value ? 'primary' : undefined}
                  sx={{ padding: '0 1rem' }}
                  variant='filled'
                  data-cy='cargo-status-empty-btn'
                  onClick={() => store.isEmpty.setValue(true)}
                />
              </Box>
            </>
          }
        >
          {!store.isEmpty.value && (
            <>
              <Stack gap={2}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      {...store.content.props}
                      fullWidth
                      label={`${t('content', 'Content')}`}
                      variant='outlined'
                      type='text'
                      name={`content`}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      {...store.grossWeight.props}
                      fullWidth
                      label={`${t('grossWeight', 'Gross weight')}`}
                      type='number'
                      required={!isFromRestowPage}
                      onKeyDown={notAllowPlusMinusPointSign}
                      onChange={e => {
                        onlyAllowPositiveInt(e)
                        store.grossWeight.onChange(e)
                      }}
                      InputProps={{
                        endAdornment: (
                          <CustomInputAdornment position='end' color='secondary'>
                            kg
                          </CustomInputAdornment>
                        ),
                      }}
                      name={`grossWeight`}
                      helperText={
                        store.grossWeight.error ? t('fieldIsRequired', 'Field is required.') : ''
                      }
                      data-cy='gross-weight-input'
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      {...store.vgm.props}
                      fullWidth
                      label={`${t('vgm', 'VGM')}`}
                      type='number'
                      required={!isFromRestowPage}
                      onKeyDown={notAllowPlusMinusPointSign}
                      onChange={e => {
                        onlyAllowPositiveInt(e)
                        store.vgm.onChange(e)
                      }}
                      InputProps={{
                        endAdornment: (
                          <CustomInputAdornment position='end' color='secondary'>
                            kg
                          </CustomInputAdornment>
                        ),
                      }}
                      name={`vgm`}
                      helperText={store.vgm.error ? t('fieldIsRequired', 'Field is required.') : ''}
                      data-cy='vgm-input'
                    />
                  </Grid>
                </Grid>
              </Stack>
            </>
          )}
        </FieldBox>
      </Stack>
    )
  },
)
